import React, { useState, useCallback } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TownPortalEmailEditor from './TownPortalEmailEditor.js';
import {
  TownPortalRecipientSection,
  emailsToTokens,
  tokensToValidEmails,
  tokensHaveInvalid,
} from './GmailRecipientField.js';
import { prepareTownPortalEmailPreview, sendTownPortalEmailFromPreview } from './SendTownPortalOffers.js';
import logger from '../../../../utils/logger';

const BLANK_EMAIL_HTML = '<p><br></p>';

const extractBodyFromIframe = () => {
  const iframe = document.querySelector('iframe[title="Town Portal Email Editor"]');
  if (!iframe?.contentDocument?.body) return null;
  return iframe.contentDocument.body.innerHTML;
};

const TownPortalEmailPreviewDialog = ({
  open,
  onClose,
  toTokens,
  setToTokens,
  ccTokens,
  setCcTokens,
  bccTokens,
  setBccTokens,
  showCcInitially,
  emailSuggestions,
  subject,
  setSubject,
  emailBody,
  editedHtml,
  setEditedHtml,
  isLoading,
  loadError,
}) => {
  const hasValidTo = toTokens.some((t) => t.valid);
  const hasInvalidRecipients =
    tokensHaveInvalid(toTokens) || tokensHaveInvalid(ccTokens) || tokensHaveInvalid(bccTokens);

  if (!open) return null;

  return (
    <StableMuiDialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '85vh', borderRadius: 1 } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #dadce0', pb: 1 }}>
        Compose Town Portal Email
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {loadError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {loadError}
            </Alert>
          )}

          {hasInvalidRecipients && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Remove or correct invalid email addresses before sending.
            </Alert>
          )}

          <Box sx={{ borderBottom: '1px solid #dadce0', mb: 1 }}>
            <TownPortalRecipientSection
              toTokens={toTokens}
              setToTokens={setToTokens}
              ccTokens={ccTokens}
              setCcTokens={setCcTokens}
              bccTokens={bccTokens}
              setBccTokens={setBccTokens}
              suggestions={emailSuggestions}
              initialShowCc={showCcInitially}
              loading={isLoading}
            />
            <TextField
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              variant="standard"
              size="small"
              autoComplete="off"
              sx={{ mb: 1 }}
            />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {isLoading ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={36} />
              </Box>
            ) : (
              <TownPortalEmailEditor emailBody={emailBody} onContentChange={setEditedHtml} />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #dadce0', px: 2, py: 1.5 }}>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onClose(true)}
          disabled={isLoading || !hasValidTo || hasInvalidRecipients}
        >
          Send Email
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

/**
 * Town portal email preview: loads assessor To and template body (portal link created on send).
 */
export const useTownPortalEmailPreview = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCallback, setDialogCallback] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [toTokens, setToTokens] = useState([]);
  const [ccTokens, setCcTokens] = useState([]);
  const [bccTokens, setBccTokens] = useState([]);
  const [showCcInitially, setShowCcInitially] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState(BLANK_EMAIL_HTML);
  const [editedHtml, setEditedHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const showDialog = useCallback(async (context, callback) => {
    const { parcelIds, linkOptions, user } = context;

    setDialogCallback(() => callback);
    setDialogOpen(true);
    setIsLoading(true);
    setLoadError(null);
    setEmailBody(BLANK_EMAIL_HTML);
    setEditedHtml('');
    setSubject('');
    setToTokens([]);
    setCcTokens([]);
    setBccTokens([]);
    setShowCcInitially(false);
    setEmailSuggestions([]);

    try {
      const prep = await prepareTownPortalEmailPreview({
        parcelIds,
        linkOptions,
        user,
      });

      if (!prep.success) {
        setLoadError(prep.error || 'Failed to load recipient addresses');
        setToTokens([]);
        const fallbackCc = user?.email ? [user.email] : [];
        setCcTokens(emailsToTokens(fallbackCc));
        setShowCcInitially(fallbackCc.length > 0);
        return;
      }

      const suggestions = prep.emailSuggestions || [];
      const loadedCc = prep.ccEmails || [];
      setEmailData(prep.emailData);
      setEmailSuggestions(suggestions);
      setToTokens(emailsToTokens(prep.toEmails || []));
      setCcTokens(emailsToTokens(loadedCc));
      setBccTokens([]);
      setShowCcInitially(loadedCc.length > 0);
      setSubject(prep.subject || '');
      setEmailBody(prep.emailBody || BLANK_EMAIL_HTML);
    } catch (err) {
      logger.error('Town portal email preview load failed:', err);
      setLoadError(err.message || 'Failed to load email preview');
      const fallbackCc = user?.email ? [user.email] : [];
      setCcTokens(emailsToTokens(fallbackCc));
      setShowCcInitially(fallbackCc.length > 0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleDialogClose = useCallback(
    (confirmed) => {
      const finalBody = extractBodyFromIframe() || editedHtml || emailBody;

      setDialogOpen(false);
      if (dialogCallback) {
        dialogCallback(confirmed, {
          toEmails: tokensToValidEmails(toTokens),
          ccEmails: tokensToValidEmails(ccTokens),
          bccEmails: tokensToValidEmails(bccTokens),
          subject,
          emailBody: finalBody,
          emailData,
        });
        setDialogCallback(null);
      }
    },
    [dialogCallback, editedHtml, emailBody, toTokens, ccTokens, bccTokens, subject, emailData]
  );

  const sendEmailFromPreview = useCallback(async (emailPreviewData) => {
    return sendTownPortalEmailFromPreview({ emailPreviewData });
  }, []);

  const dialogComponent = (
    <TownPortalEmailPreviewDialog
      open={dialogOpen}
      onClose={handleDialogClose}
      toTokens={toTokens}
      setToTokens={setToTokens}
      ccTokens={ccTokens}
      setCcTokens={setCcTokens}
      bccTokens={bccTokens}
      setBccTokens={setBccTokens}
      showCcInitially={showCcInitially}
      emailSuggestions={emailSuggestions}
      subject={subject}
      setSubject={setSubject}
      emailBody={emailBody}
      editedHtml={editedHtml}
      setEditedHtml={setEditedHtml}
      isLoading={isLoading}
      loadError={loadError}
    />
  );

  return {
    showDialog,
    sendEmailFromPreview,
    dialogComponent,
  };
};
